import * as React from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type PageActionItem = {
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    to?: string;
    disabled?: boolean;
    variant?: 'default' | 'destructive';
    dataTest?: string;
};

export type PageActionGroup = {
    label?: string;
    items: PageActionItem[];
};

type Props = {
    label?: string;
    groups: PageActionGroup[];
    align?: 'start' | 'center' | 'end';
};

export const PageActionsMenu = ({ label = 'Actions', groups, align = 'end' }: Props) => {
    const hasContent = groups.some((group) => group.items.length > 0);
    if (!hasContent) {
        return null;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button size="lg" variant="outline" data-test="page-actions-menu-trigger">
                    <MoreHorizontal />
                    {label}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={align} className="min-w-56" data-test="page-actions-menu">
                {groups.map((group, groupIndex) => {
                    if (group.items.length === 0) {
                        return null;
                    }
                    return (
                        <React.Fragment key={groupIndex}>
                            {groupIndex > 0 ? <DropdownMenuSeparator /> : null}
                            {group.label ? <DropdownMenuLabel>{group.label}</DropdownMenuLabel> : null}
                            {group.items.map((item, itemIndex) => {
                                const content = (
                                    <>
                                        {item.icon}
                                        <span>{item.label}</span>
                                    </>
                                );
                                if (item.to) {
                                    return (
                                        <DropdownMenuItem
                                            key={itemIndex}
                                            asChild
                                            disabled={item.disabled}
                                            variant={item.variant}
                                        >
                                            <Link to={item.to} data-test={item.dataTest}>
                                                {content}
                                            </Link>
                                        </DropdownMenuItem>
                                    );
                                }
                                return (
                                    <DropdownMenuItem
                                        key={itemIndex}
                                        onSelect={item.onClick}
                                        disabled={item.disabled}
                                        variant={item.variant}
                                        data-test={item.dataTest}
                                    >
                                        {content}
                                    </DropdownMenuItem>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
